import { getProductPrice } from './vatUtils';
import { useTranslation } from 'react-i18next';
import { EmailProductData } from '../../stores/appOrderHistoryStore';
import { Platform } from '../../types/Platforms';
import { formatQuantityInfo } from '../../utils/quantityUtils';
import { calculateProductTotal } from '../../utils/platformUtils';
import { calculateUnitPrice } from './aggregations';
import { useAppSettingsStore } from '../../stores/appSettingsStore';
import { renderByType } from '../ui/TableValueComponent';

// Component to display individual product
export const ProductDataItem = ({ product, platform, canEdit, onQuantityClick, showExVat = true, responsive = false }: { product: EmailProductData, platform: Platform, canEdit?: boolean, showExVat?: boolean, responsive?: boolean, onQuantityClick?: (product: EmailProductData) => void }) => {
  const { t } = useTranslation();
  const darkMode = useAppSettingsStore((s) => s.darkMode);

  var productTotal = calculateProductTotal(product, platform, showExVat);

  // Use rawQuantity for quantity display to match the aggregation logic
  const quantityForDisplay = product.rawQuantity || product.quantity;
  const quantityDisplay = product.quantityInfo ? formatQuantityInfo(quantityForDisplay, product.quantityInfo, product.category) : null;

  // Calculate unit price using same logic as aggregations.ts (shown for all users)
  const unitPrice = calculateUnitPrice(getProductPrice(product, showExVat), product.quantityInfo, product.category);

  return (
    <div className={`flex justify-between p-3 bg-background dark:bg-background-dark hover:bg-muted/10 dark:hover:bg-muted-dark/10 rounded-xl border border-border dark:border-border-dark transition-colors ${responsive ? 'flex-col sm:flex-row sm:items-center gap-3 sm:ml-6' : 'items-center ml-6'}`}>
      <div className={responsive ? "flex-1 min-w-0 break-words" : "flex-1"}>
        {quantityDisplay && (
          <div className={`mb-2 flex items-center gap-2 ${responsive ? 'flex-wrap' : ''}`}>
            <span
              className={`inline-block px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-medium rounded-md${canEdit ? ' cursor-pointer hover:underline' : ''}`}
              onClick={canEdit ? () => onQuantityClick?.(product) : undefined}
              title={canEdit ? t('orderHistory.quantityInfoEdit.title') : undefined}
            >
              {quantityDisplay.displayText}
            </span>
            {unitPrice && (
              <span className="inline-block px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-xs font-medium rounded-md">
                €{unitPrice.displayText}
              </span>
            )}
            {product.category && (
              <span className="inline-block">
                {renderByType(product.category, 'category', undefined, undefined, undefined, undefined, undefined, darkMode)}
              </span>
            )}
          </div>
        )}
        {!quantityDisplay && product.category && (
          <div className="mb-2">
            <span className="inline-block">
              {renderByType(product.category, 'category', undefined, undefined, undefined, undefined, undefined, darkMode)}
            </span>
          </div>
        )}
        <div className="font-semibold text-text dark:text-text-dark text-base">
          {product.name || t('invoices.unknownProduct')}
        </div>
        {product.articleId && product.articleId !== 'no_article_id' && (
          <div className="text-sm text-muted dark:text-muted-dark font-mono mt-1 flex items-center gap-3">
            <span>ID: {product.articleId}</span>
            {product.rawQuantity !== undefined && (
              <span>{t('orderHistory.quantity')}: {product.rawQuantity}</span>
            )}
          </div>
        )}
      </div>
      <div className="text-right ml-4">
        <div className="text-lg font-bold text-text dark:text-text-dark">
          €{productTotal.toFixed(2)}
        </div>
        <div className="text-sm text-muted dark:text-muted-dark font-medium">
          {product.quantity.toFixed(1)} × €{(getProductPrice(product, showExVat) || 0).toFixed(2)}
        </div>
      </div>
    </div>
  );
};

